import './HobbitHole.css';
import Navbar from '../components/Navbar';
import hobbitlora from '../assets/images/hobbitlora.gif';
import { useState, useEffect } from 'react';
import { URL, IMAGE_MAP } from '../components/constants';
import Event from '../components/Event';

export default function HobbitHole() {
  const [userDetails, setUserDetails] = useState(null);
  const [allEvents, setAllEvents] = useState({});
  const [registeredEvents, setRegisteredEvents] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        // User info
        const meRes = await fetch(`${URL}/me`, { credentials: "include" });
        if (meRes.status !== 200) throw new Error("Not logged in");
        const meData = await meRes.json();
        setUserDetails(meData.details);

        // All events
        const eventsRes = await fetch(`${URL}/allevents`, { credentials: "include" });
        if (eventsRes.status !== 200) throw new Error("Failed to fetch events");
        const eventsData = await eventsRes.json();
        setAllEvents(eventsData);

        // Registered events
        const regRes = await fetch(`${URL}/registered_events`, { credentials: "include" });
        if (regRes.status !== 200) throw new Error("Failed to fetch registered events");
        const regData = await regRes.json();
        setRegisteredEvents(new Set(regData));
      } catch (err) {
        console.error(err);
        setUserDetails({ nickname: "Unknown", description: "" });
        setAllEvents({});
        setRegisteredEvents(new Set());
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  function getDayOfWeek(dateStr) {
    const [month, day] = dateStr.split("/");
    const d = new Date(2026, month - 1, day); // year doesn't matter much
    return d.toLocaleDateString("en-US", { weekday: "long" });
  }

  if (loading) return <div><Navbar /></div>;

  // Split events into itinerary vs other
  const itineraryEvents = [];
  const otherEvents = [];
  Object.entries(allEvents).forEach(([key, event]) => {
    const isRegistered = registeredEvents.has(key);
    if (event.mandatory || isRegistered) {
      itineraryEvents.push({ key, event, isRegistered });
    } else {
      otherEvents.push({ key, event, isRegistered });
    }
  });

  // Sort events by start date (string) — safe since all in same month
  itineraryEvents.sort((a, b) => a.event.order - b.event.order);
  otherEvents.sort((a, b) => a.event.order - b.event.order);

  // Callback to update registeredEvents state when Event registers/unregisters
  const handleRegistrationChange = (eventId, registered) => {
    // Update the set of events I'm registered for
    setRegisteredEvents(prev => {
      const updated = new Set(prev);
      if (registered) updated.add(eventId);
      else updated.delete(eventId);
      return updated;
    });

    // Update the registered users list for the event
    setAllEvents(prev => {
      const updated = { ...prev };
      const event = { ...updated[eventId] };

      const users = event.users_registered ? [...event.users_registered] : [];

      if (registered) {
        // Add me if not already present
        if (!users.some(u => u.nickname === userDetails.nickname)) {
          users.push({
            nickname: userDetails.nickname,
            image: userDetails.image
          });
        }
      } else {
        // Remove me
        event.users_registered = users.filter(
          u => u.nickname !== userDetails.nickname
        );
        updated[eventId] = event;
        return updated;
      }

      event.users_registered = users;
      updated[eventId] = event;
      return updated;
    });
  };

  return (
    <div>
      <Navbar />
      <h1 style={{ flexBasis: "100%", textAlign: "center", marginTop:"40px" }}>
        {userDetails.nickname}
      </h1>

      <div className="hobbithole-body">
        <div className="profpicbox">
          <img src={IMAGE_MAP[userDetails.image] || hobbitlora} className="profpic" alt="" />
        </div>
        <div className="description">
          {userDetails.description}
        </div>
      </div>

      <div className="moretext">
        <h1>My Events</h1>
        Mandatory events and events that you have registered for
        {itineraryEvents.map(({ key, event, isRegistered }, index) => {
          const currentDate = event.start_time_nz.split(" ")[0];

          let showDateHeader = false;
          if (index === 0) {
            showDateHeader = true;
          } else {
            const prevDate = itineraryEvents[index - 1].event.start_time_nz.split(" ")[0];
            showDateHeader = currentDate !== prevDate;
          }

          return (
            <div key={key}>
              {showDateHeader && (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <h3>{getDayOfWeek(currentDate)} — {currentDate}</h3>
                  <hr style={{ flex: 1, border: "none", borderTop: "4px solid #ADB68C" }} />
                </div>
              )}

              <Event
                id={key}
                name={event.title}
                startTime={event.start_time_nz}
                endTime={event.end_time_nz}
                description={event.description}
                location={event.place}
                mandatory={event.mandatory}
                register={event.register}
                initial_registered={isRegistered}
                onRegistrationChange={handleRegistrationChange}
                registeredUsers={event.users_registered}
              />
            </div>
          );
        })}
        <br />
        <hr style={{ flex: 1, border: "none", borderTop: "5px solid #ADB68C" }} />

        <br /><br /><br />
        <h1>Unregistered Events</h1>
        Events that you don't plan on attending
        {otherEvents.map(({ key, event, isRegistered }) => (
          <Event
            key={key}
            id={key}
            name={event.title}
            startTime={event.start_time_nz}
            endTime={event.end_time_nz}
            description={event.description}
            location={event.place}
            mandatory={event.mandatory}
            register={event.register}
            initial_registered={isRegistered}
            onRegistrationChange={handleRegistrationChange} // ✅ pass callback
            registeredUsers={event.users_registered}
          />
        ))}
      </div>
    </div>
  );
}